// editLease.js
// The advisory edit lease shared by the editors that work on one Firestore
// document at a time: the Function builder (the draft head document) and the
// View builder (the view document). It lives in the document's `edit_lease`
// field, prevents accidental co-editing through the UI, can always be taken
// over with confirmation, and expires on its own when a session crashes.
// Nothing about execution or rendering depends on it.
//
// Every function takes the document reference the lease lives on; `noun`
// names the document in the "no longer exists" error ('function', 'view').

import { assertResourceAvailable, isResourceDeleting } from '../utils/resourceLifecycle';
import { formatTimestamp } from '../utils/timestamps';
import { runTransaction, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { invalidateAssistantRuns } from './assistantRunLease';

// Lease lifetime and heartbeat cadence (function_versions section 2.4).
export const LEASE_TTL_MS = 15 * 60 * 1000;
export const LEASE_HEARTBEAT_MS = 3 * 60 * 1000;

// --- Session mounts ---------------------------------------------------------
// A session id identifies one EDITING SESSION — the editor that owns it mints
// it when the session starts and drops it when the session ends — and an editor
// is remounted by development refreshes and by any re-render that drops it. The
// remount rejoins the same session, because acquireEditLease hands a session its
// own lease back, so the mount it replaced must not hand that lease over on its
// way out. These record which mount is currently editing one session OF one
// document: `sessionKey` is the session id and the document together, so an
// editor that moves to another document still hands back the lease it leaves.
const mountsBySessionKey = new Map();

// Register this mount as the one editing `sessionKey`, returning the token its
// release must present.
export function registerSessionMount(sessionKey) {
  const token = {};
  mountsBySessionKey.set(sessionKey, token);
  return token;
}

// True while `token` is still the mount editing `sessionKey`. It is consumed:
// the caller is leaving for good, so the key is left unclaimed.
export function consumeSessionMount(sessionKey, token) {
  if (mountsBySessionKey.get(sessionKey) !== token) return false;
  mountsBySessionKey.delete(sessionKey);
  return true;
}

const leaseExpiryMillis = (lease) => {
  const expiresAt = lease?.expires_at;
  if (expiresAt?.toMillis) return expiresAt.toMillis();
  if (expiresAt?.toDate) return expiresAt.toDate().getTime();
  const parsed = expiresAt ? new Date(expiresAt).getTime() : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

// A lease is available when it is absent, expired, or already ours.
const leaseIsAvailable = (lease, uid, sessionId) => {
  if (!lease) return true;
  if (leaseExpiryMillis(lease) < Date.now()) return true;
  return lease.holder_uid === uid && lease.session_id === sessionId;
};

// `acquired_at` and `renewed_at` are written by Firestore, not by this browser:
// they are read against leases other machines wrote, and a clock a few seconds
// out would otherwise make one session's lease look older or newer than it is.
// `expires_at` cannot be one of them — a client write cannot ask the server for
// its own clock plus fifteen minutes — so liveness is still decided from an
// instant this browser computed, here and in the backend (validate_data_function
// and the Logic Assistant run lease both read this field).
const freshLease = (uid, holderName, sessionId) => ({
  holder_uid: uid,
  holder_name: holderName,
  session_id: sessionId,
  acquired_at: serverTimestamp(),
  renewed_at: serverTimestamp(),
  expires_at: Timestamp.fromDate(new Date(Date.now() + LEASE_TTL_MS)),
});

// When the holder started, for the blocked dialog.
export const leaseStartedLabel = (lease) => {
  const acquiredAt = lease?.acquired_at;
  const date = acquiredAt?.toDate ? acquiredAt.toDate() : (acquiredAt ? new Date(acquiredAt) : null);
  if (!date || Number.isNaN(date.getTime())) return 'just now';
  return formatTimestamp(date);
};

// A successful claim returns the document read in the same transaction, so an
// editor initializes from the content at the handoff. A refusal returns
// { granted: false, lease } with the holder shown in the blocked dialog.
export async function acquireEditLease(ref, { uid, holderName, sessionId }, noun) {
  return runTransaction(db, async (tx) => {
    const snapshot = await tx.get(ref);
    if (!snapshot.exists()) throw new Error(`This ${noun} no longer exists.`);
    assertResourceAvailable(snapshot, noun);
    const data = snapshot.data();
    const lease = data?.edit_lease || null;
    if (!leaseIsAvailable(lease, uid, sessionId)) {
      return { granted: false, lease };
    }
    if (noun === 'function' && leaseExpiryMillis(lease) <= Date.now()) {
      await invalidateAssistantRuns(tx, ref, data, lease?.session_id);
    }
    tx.update(ref, { edit_lease: freshLease(uid, holderName, sessionId) });
    return { granted: true, lease: null, data };
  });
}

// Take the lease from whoever holds it. Always behind an explicit confirmation.
export async function takeOverEditLease(ref, { uid, holderName, sessionId }, noun) {
  return runTransaction(db, async (tx) => {
    const snapshot = await tx.get(ref);
    if (!snapshot.exists()) throw new Error(`This ${noun} no longer exists.`);
    assertResourceAvailable(snapshot, noun);
    if (noun === 'function') {
      const data = snapshot.data();
      await invalidateAssistantRuns(tx, ref, data, data.edit_lease?.session_id);
    }
    tx.update(ref, { edit_lease: freshLease(uid, holderName, sessionId) });
    return { granted: true, data: snapshot.data() };
  });
}

// Extend our lease. Returns 'held' while the lease is still ours, 'lost' when
// another session took it over, or 'deleted' when the document itself is gone —
// in both of the latter cases this editor must close, but they read differently
// to the person sitting at it.
export async function heartbeatEditLease(ref, sessionId) {
  return runTransaction(db, async (tx) => {
    const snapshot = await tx.get(ref);
    if (!snapshot.exists()) return 'deleted';
    const data = snapshot.data();
    if (isResourceDeleting(data) && data.deletion_started_at) return 'deleted';
    const lease = data?.edit_lease || null;
    if (!lease || lease.session_id !== sessionId) return 'lost';
    // The provisional freeze may be refused without removing anything. Keep
    // the draft open, but do not renew a lease on a frozen document.
    if (isResourceDeleting(data)) return 'held';
    tx.update(ref, {
      'edit_lease.renewed_at': serverTimestamp(),
      'edit_lease.expires_at': Timestamp.fromDate(new Date(Date.now() + LEASE_TTL_MS)),
    });
    return 'held';
  });
}

// Release on a clean close. A lease already taken over by someone else is left
// exactly as it is.
export async function releaseEditLease(ref, sessionId, noun) {
  return runTransaction(db, async (tx) => {
    const snapshot = await tx.get(ref);
    if (!snapshot.exists() || isResourceDeleting(snapshot.data())) return;
    const lease = snapshot.data()?.edit_lease || null;
    if (!lease || lease.session_id !== sessionId) return;
    if (noun === 'function') {
      await invalidateAssistantRuns(tx, ref, snapshot.data(), sessionId);
    }
    tx.update(ref, { edit_lease: null });
  });
}
